/**
 * Origin taxonomy for tags — ONE shared classification for the tags list,
 * histories and the Smart POS. deviceKind is authoritative; the legacy
 * template inference retires the day pax/corevend templates gain
 * deviceKind: 'terminal'.
 */
// Third-party terminal PROVIDERS: real terminals, never Smart-POS-driveable
// (the hand-off machinery is Stripe-only). TWO families (Charles,
// 2026-10-04): pax/corevend stay "Tipping Terminals"; ecomm is its own
// "Pay + Tip Terminals" family.
export const LEGACY_TERMINAL_TEMPLATES = ['pax', 'corevend'];
export const PAYTIP_TERMINAL_TEMPLATES = ['ecomm'];

/**
 * The QR device family — physical form factors that behave identically
 * (armable by the Smart POS, bare-scan binding); they differ only in how
 * people THINK about them: a stand sits on a counter, a sticker sticks to
 * a window, a card or fob travels with a person.
 */
export const QR_DEVICE_KINDS = ['stand', 'sticker', 'card', 'fob'];
export const isQrDeviceKind = (kind) => QR_DEVICE_KINDS.includes(kind);

// "Smart X" names the device as a member of the Smart POS family (Charles,
// 2026-08-27 — the hand-off word stays with the ACTION, not the hardware).
export const DEVICE_KIND_LABELS = {
  terminal: 'Smart Terminal',
  stand: 'Smart Stand',
  sticker: 'Smart Sticker',
  card: 'Smart Card',
  fob: 'Smart Fob',
};
export const deviceKindLabel = (kind) => DEVICE_KIND_LABELS[kind] || 'Smart Stand';

export const DEVICE_CATEGORIES = {
  'handoff-terminal': { label: 'Smart Terminals', description: 'Terminals the Smart POS can drive' },
  'handoff-stand': { label: 'Smart QRs', description: 'Stands, stickers, cards and fobs the Smart POS can arm' },
  'tipping-terminal': { label: 'Tipping Terminals', description: 'Standalone tipping terminals' },
  'paytip-terminal': { label: 'Pay + Tip Terminals', description: 'Third-party pay + tip terminals' },
  qr: { label: 'QR Codes', description: 'Printed and shared QR codes' },
};

export const DEVICE_CATEGORY_ORDER = ['handoff-terminal', 'handoff-stand', 'tipping-terminal', 'paytip-terminal', 'qr'];

/**
 * A Stripe Smart Terminal's tag is minted `${template}---${tmr_…}` (its reader
 * id is the uid), so a tap/activity row can be classed from the tag id alone —
 * no template lookup, no deviceKind on the tap needed.
 */
export const isStripeTerminalTagId = (tagId) => String(tagId || '').includes('---tmr_');
export const isStripeTerminalTagUid = (tagUid) => String(tagUid || '').startsWith('tmr_');

export function deviceCategoryKey(tag) {
  if (!tag) return 'qr';
  if (tag.deviceKind === 'terminal') return 'handoff-terminal';
  if (isQrDeviceKind(tag.deviceKind)) return 'handoff-stand';
  if (LEGACY_TERMINAL_TEMPLATES.includes(tag.templateTagId)) return 'tipping-terminal';
  if (PAYTIP_TERMINAL_TEMPLATES.includes(tag.templateTagId)) return 'paytip-terminal';
  return 'qr';
}

export function deviceCategory(tag) {
  const key = deviceCategoryKey(tag);
  return { key, ...DEVICE_CATEGORIES[key] };
}


// One icon vocabulary for every surface that renders a device/tag (Smart POS
// wall, team tags list, …): template signupImage first, then this kind glyph.
import {
  IdcardOutlined, KeyOutlined, PushpinOutlined, QrcodeOutlined, TabletOutlined,
} from '@ant-design/icons';

export const DEVICE_KIND_ICONS = {
  terminal: TabletOutlined,
  stand: QrcodeOutlined,
  sticker: PushpinOutlined,
  card: IdcardOutlined,
  fob: KeyOutlined,
};

export const deviceKindIcon = (kind) => DEVICE_KIND_ICONS[kind] || QrcodeOutlined;
